/**
 * tabUsage — which gym-page views this device opened recently
 * (docs/spec/01f-tab-loading.md, "Background data prefetch by usage").
 *
 * `localStorage["sendly-tab-usage"] = { [gymId]: { [viewKey]: lastOpenedMs } }`.
 * A view key is a tab key, plus `adminStats` for the admin Statistics section.
 * It orders the code warm-up and picks what the background prefetch reads, so
 * a climber who only ever opens the map and Statistics pays for nothing else.
 *
 * Every access is wrapped: a private window, blocked storage or a value written
 * by an older build means "no usage", never an error on the gym page.
 */
import { localStore } from "./safeStorage";

export const TAB_USAGE_KEY = "sendly-tab-usage";
export const TAB_USAGE_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;

function readAll() {
  const parsed = localStore.getJSON(TAB_USAGE_KEY);
  return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
}

/** Record that `view` was opened on `gymId` now; prunes entries past the window. */
export function recordViewOpen(gymId, view, now = Date.now()) {
  if (!gymId || !view) return;
  const all = readAll();
  const pruned = {};
  for (const [gym, views] of Object.entries(all)) {
    if (!views || typeof views !== "object") continue;
    const kept = {};
    for (const [key, at] of Object.entries(views)) {
      if (Number.isFinite(at) && now - at < TAB_USAGE_WINDOW_MS) kept[key] = at;
    }
    if (Object.keys(kept).length) pruned[gym] = kept;
  }
  pruned[gymId] = { ...(pruned[gymId] || {}), [view]: now };
  // Usage is a convenience: a refused write just means no usage is remembered.
  localStore.setJSON(TAB_USAGE_KEY, pruned);
}

/** The views of `gymId` opened within the window, most recent first. */
export function readUsedViews(gymId, now = Date.now()) {
  if (!gymId) return [];
  const views = readAll()[gymId];
  if (!views || typeof views !== "object") return [];
  return Object.entries(views)
    .filter(([, at]) => Number.isFinite(at) && now - at < TAB_USAGE_WINDOW_MS)
    .sort((a, b) => b[1] - a[1])
    .map(([view]) => view);
}

/**
 * The warm-up order: the used tabs that are in the bar (most recent first),
 * then the other bar tabs in bar order. The active tab and the map (main
 * bundle) are left out.
 */
export function orderWarmupTabs(barTabKeys, usedViews, activeTab) {
  const inBar = new Set(barTabKeys);
  const skip = (key) => key === activeTab || key === "map" || !inBar.has(key);
  const used = usedViews.filter((key) => !skip(key));
  const rest = barTabKeys.filter((key) => !skip(key) && !used.includes(key));
  return [...used, ...rest];
}

/** `navigator.connection.saveData` — iOS Safari never reports it. */
export function saveDataOn() {
  try {
    return typeof navigator !== "undefined" && navigator.connection?.saveData === true;
  } catch {
    return false;
  }
}
